import {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch} from 'react-redux';
import {DragEndEvent} from '@dnd-kit/core';
import {arrayMove} from '@dnd-kit/sortable';
import {parseValidationErrors} from 'services/api/utils/parse-error';
import {
  useGetHomePageSettingsQuery,
} from 'store/global/endpoints/get-home-page-settings';
import {
  UpdateHomePageSettingsRequest,
  useUpdateHomePageSettingsMutation,
} from 'store/global/endpoints/update-home-page-settings';
import {useUploadTemporaryFileMutation} from 'store/global/endpoints/upload-temporary-file';
import {HomePageSection} from 'types/home-page';
import toast from 'store/ui/actions/toast';
import {SECTION_ORDER} from './messages';

const emptySection = (slug: string): HomePageSection => ({
  section: slug,
  order: 0,
  title: {fr: '', en: ''},
});

export default () => {
  const {data, isLoading: isLoadingData} = useGetHomePageSettingsQuery();
  const [submit, {isLoading: isSubmitting, error}] =
    useUpdateHomePageSettingsMutation();
  const [sendTempFile, {isLoading: isUploading}] =
    useUploadTemporaryFileMutation();

  const serverError = parseValidationErrors(error);

  const [sections, setSections] = useState<HomePageSection[]>([]);
  const {t} = useTranslation();
  const dispatch = useDispatch();

  useEffect(() => {
    if (data?.data?.sections) {
      const serverSections = [...data.data.sections].sort(
        (a, b) => a.order - b.order
      );
      const merged = serverSections.map(s => ({
        ...s,
        section: s.section ?? '',
      }));
      for (const slug of SECTION_ORDER) {
        if (!merged.find(s => s.section === slug)) {
          merged.push({...emptySection(slug), order: merged.length + 1});
        }
      }
      setSections(merged);
    } else if (data) {
      setSections(SECTION_ORDER.map((slug, i) => ({...emptySection(slug), order: i + 1})));
    }
  }, [data]);

  const updateSection = (
    index: number,
    key: keyof HomePageSection | 'title.fr' | 'title.en',
    value: any
  ) => {
    setSections(prev => {
      const next = [...prev];
      if (!next[index]) {
        next[index] = {section: SECTION_ORDER[index] ?? '', order: index + 1, title: {fr: '', en: ''}};
      }
      if (key === 'title.fr') {
        next[index] = {
          ...next[index],
          title: {...next[index].title, fr: value},
        };
      } else if (key === 'title.en') {
        next[index] = {
          ...next[index],
          title: {...next[index].title, en: value},
        };
      } else {
        next[index] = {...next[index], [key]: value};
      }
      return next;
    });
  };

  const removeSectionImage = (index: number) => {
    setSections(prev => {
      const next = [...prev];
      if (!next[index]) {
        return prev;
      }
      next[index] = {
        ...next[index],
        file: undefined,
        image: undefined,
        media_id: null,
      };
      return next;
    });
  };

  const handleUploadImage = async (index: number, file: File) => {
    const tempFileResult = await sendTempFile({data: {file}});
    if ('data' in tempFileResult) {
      updateSection(index, 'file', tempFileResult.data.data);
    } else {
      dispatch(toast('error', 'Failed to upload image'));
    }
  };

  const onDragEnd = (event: DragEndEvent) => {
    const {active, over} = event;
    if (!over || active.id === over.id) return;
    setSections(prev => {
      const oldIndex = prev.findIndex(s => s.section === active.id);
      const newIndex = prev.findIndex(s => s.section === over.id);
      if (oldIndex === -1 || newIndex === -1) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  const handleSubmit = async () => {
    const body: UpdateHomePageSettingsRequest = {
      sections: sections.map((s, i) => ({
        order: i + 1,
        section: s.section,
        title: s.title,
        file_id: s.file?.id ?? s.media_id ?? null,
      })),
    };
    const result = await submit(body);
    if ('data' in result) {
      dispatch(toast('success', t('common.changesCommited')));
    } else {
      const {message} = parseValidationErrors(result.error);
      dispatch(toast('error', message));
    }
  };

  return {
    sections,
    updateSection,
    removeSectionImage,
    handleUploadImage,
    handleSubmit,
    onDragEnd,
    isLoading: isLoadingData || isSubmitting,
    isUploading,
    validationErrors: serverError.errors,
    errorMessage: serverError.message,
  };
};
